import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { Icon } from '@/ux-common/components/icon/icon';
import { cn } from '@/ux-common/lib/utils';

/**
 * Accordion — minimal Radix wrappers for a collapsible container with a
 * clickable anchor area.
 *
 * An accordion is nothing more than a collapsible container with a clickable
 * anchor. Rather than ship bespoke header markup, the registry accordion
 * **embeds the registry `Header`** inside the trigger: the consumer composes a
 * `<Header>` (with its `text-*-header` Typography tier) inside
 * `<AccordionTrigger>`, and the accordion contributes only the collapse
 * behavior plus an auto-rendered chevron. All header look-and-feel continues to
 * live in the Header component and its typography classes; the Titan theme adds
 * a compound layer (`themes/titan/components/accordion.css`) that re-skins the
 * embedded header for the accordion context (subsection background, brand
 * open-state foreground) keyed off the header's `text-*-header` class.
 *
 * The four exports are thin: each renders the matching Radix primitive with a
 * base class auto-applied, `cn()` className merging, ref forwarding, full
 * attribute passthrough, and a `data-component` identifier. `AccordionTrigger`
 * is the only one with structure — it renders
 * `AccordionPrimitive.Header > AccordionPrimitive.Trigger`, places `{children}`,
 * then appends the chevron as a registry `Icon` box wrapping a `ChevronDown`
 * glyph (`<Icon className="accordion-chevron"><ChevronDown className="glyph glyph-sm" /></Icon>`).
 * Modeling the chevron on the Icon container lets the Titan theme give it the
 * Figma chevron treatment per tier — a transparent box that flips to a
 * high-contrast "chip" (white bg, inverse glyph) when open for the category and
 * count tiers, and a bare leading glyph for the nav tier. Rotation is driven
 * purely by CSS off Radix's `data-state`; the per-tier look lives in the theme.
 *
 * **Class API** (owned by `theme-default`, restyled by `titan`):
 * - `.accordion` — root container (vertical stack)
 * - `.accordion-item` — one collapsible unit
 * - `.accordion-trigger` — the clickable anchor; a flex row that contains a `.header`
 * - `.accordion-chevron` — auto-rendered chevron slot
 * - `.accordion-content` — collapsible body (Radix height animation)
 * - `.accordion-trigger-leading` — modifier: chevron on the leading edge (tree/list)
 *
 * @example
 * ```tsx
 * <Accordion type="single" collapsible>
 *   <AccordionItem value="compressors">
 *     <AccordionTrigger>
 *       <Header>
 *         <HeaderIcon><Gauge aria-hidden /></HeaderIcon>
 *         <HeaderTitle>
 *           <Typography as="h3" className="text-sub-header">Compressors</Typography>
 *         </HeaderTitle>
 *         <HeaderActions><Button>Add</Button></HeaderActions>
 *       </Header>
 *     </AccordionTrigger>
 *     <AccordionContent>…</AccordionContent>
 *   </AccordionItem>
 * </Accordion>
 *
 * // Tree / list style — chevron on the leading edge
 * <AccordionTrigger className="accordion-trigger-leading">
 *   <Header>
 *     <HeaderTitle>
 *       <Typography as="span" className="text-nav-header">Section</Typography>
 *     </HeaderTitle>
 *   </Header>
 * </AccordionTrigger>
 * ```
 */
/**
 * Distributive `Omit` — removes `K` from every member of a union while keeping
 * the union intact (plain `Omit` would collapse Root's `single | multiple`
 * discriminated union). Used to drop the unsupported `disabled` prop.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/**
 * NOTE: `disabled` is intentionally **not supported** on the accordion (Root,
 * Item, or Trigger) — a disabled collapsible causes more problems than it solves
 * (pointer-event/tooltip handling, focus management on the non-button trigger).
 * It is stripped from the prop types below. Gate availability upstream (don't
 * render the item) instead of disabling it.
 */
export type AccordionProps = DistributiveOmit<
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>,
  'disabled'
>;

export const Accordion = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Root>,
  AccordionProps
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Root
    ref={ref}
    data-component="Accordion"
    className={cn('accordion', className)}
    {...props}
  />
));
Accordion.displayName = 'Accordion';

/**
 * AccordionItem — one collapsible unit. Renders `AccordionPrimitive.Item` with
 * the `.accordion-item` class auto-applied.
 */
export type AccordionItemProps = Omit<
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>,
  'disabled'
>;

export const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  AccordionItemProps
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    data-component="AccordionItem"
    className={cn('accordion-item', className)}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

/**
 * AccordionTrigger — the clickable anchor. Renders
 * `AccordionPrimitive.Header > AccordionPrimitive.Trigger` where the Trigger uses
 * Radix's `asChild` to render a `<div role="button" tabIndex={0}>` (not a native
 * `<button>`), so the embedded Header may contain its own interactive controls
 * (action Buttons, links) without nesting a button inside a button. Places the
 * consumer-composed `{children}` (typically a `<Header>`), then auto-appends the
 * chevron as `<Icon className="accordion-chevron"><ChevronDown className="glyph glyph-sm" /></Icon>`.
 * Consumers never add the chevron. Keyboard activation (Enter/Space) and click
 * isolation for nested controls are handled internally; rotation and per-tier
 * styling are CSS-only. The ref forwards to the underlying `<div>`.
 */
export type AccordionTriggerProps = Omit<
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>,
  'asChild'
> &
  Pick<React.HTMLAttributes<HTMLDivElement>, 'onKeyDown'>;

export const AccordionTrigger = React.forwardRef<HTMLDivElement, AccordionTriggerProps>(
  ({ className, children, onClick, onKeyDown, ...props }, ref) => {
    // Rendered as a non-button (a <div role="button">) via Radix's `asChild`, so
    // the embedded Header may contain its own interactive controls (action
    // Buttons, links, inputs) without nesting a <button> inside a <button>.
    //
    // Because it's not a native button, two things are restored by hand:
    //  1. Keyboard activation — Enter/Space toggles when focus is on the trigger
    //     itself (not on a nested control). Radix still owns Arrow/Home/End
    //     navigation between triggers via its own composed onKeyDown.
    //  2. Click isolation — a click that originated on an interactive descendant
    //     calls preventDefault, which Radix honors (it composes our onClick with
    //     its toggle via checkForDefaultPrevented) and skips the toggle.
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;
      const target = event.target as HTMLElement;
      const currentTarget = event.currentTarget as HTMLElement;
      // React bubbles events through the React tree, not the DOM tree, so a
      // click on a *portaled* descendant — e.g. a dropdown menu item whose
      // content renders into document.body but whose React parent is a control
      // embedded in this trigger — reaches this handler with a `target` that
      // lives outside the trigger's DOM subtree. That is never a click on the
      // toggle affordance, so skip the toggle. (DOM `closest()` below can't see
      // it: it walks the portaled target's DOM ancestors, not this trigger.)
      if (!currentTarget.contains(target)) {
        event.preventDefault();
        return;
      }
      // Within the trigger: a click that landed on a nested interactive control
      // (action Button, link, input) should operate that control, not toggle.
      const interactive = target.closest(
        'button, a, [role="button"], input, select, textarea, label'
      );
      if (interactive && interactive !== currentTarget) {
        event.preventDefault();
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;
      // Only self-activate; let nested controls handle their own keys.
      if ((event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) {
        event.preventDefault();
        event.currentTarget.click();
      }
    };

    return (
      <AccordionPrimitive.Header className="accordion-header">
        <AccordionPrimitive.Trigger asChild onClick={handleClick} {...props}>
          <div
            ref={ref}
            role="button"
            tabIndex={0}
            data-component="AccordionTrigger"
            className={cn('accordion-trigger', className)}
            onKeyDown={handleKeyDown}
          >
            {children}
            <Icon className="accordion-chevron" data-component="AccordionChevron" aria-hidden>
              <ChevronDown className="glyph glyph-sm" aria-hidden />
            </Icon>
          </div>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    );
  }
);
AccordionTrigger.displayName = 'AccordionTrigger';

/**
 * AccordionContent — the collapsible body. Renders `AccordionPrimitive.Content`
 * with the `.accordion-content` class auto-applied; the height animation is
 * handled in the theme CSS via Radix's `--radix-accordion-content-height`.
 */
export const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    data-component="AccordionContent"
    className={cn('accordion-content', className)}
    {...props}
  />
));
AccordionContent.displayName = 'AccordionContent';
